import type { ImageConfig } from '../interfaces';
import { ImageConfigType } from '../interfaces';

import { debounce } from './debounce';

const imageSizesConfig: ImageConfig[] = window.imageSizesConfig ?? [
  {
    match: /img(?:[-.]\w+)?\.uefa\.com\/imgml\/TP\/teams\/logos\/(\d+x\d+)\//,
    sizes: [
      // Clubs
      18, 32, 36, 50, 64, 70, 100, 140, 240, 700,
    ],
    type: ImageConfigType.PATH,
    path: /\/\d+x\d+\//,
  },
  {
    match: /img(?:[-.]\w+)?\.uefa\.com\/imgml\/flags\/(\d+x\d+)\//,
    sizes: [
      // Teams
      18, 32, 36, 50, 64, 70, 100, 140, 240, 700,
    ],
    type: ImageConfigType.PATH,
    path: /\/\d+x\d+\//,
  },
  {
    match: /img(?:[-.]\w+)?\.uefa\.com\/imgml\/TP\/players\/\d+\/\d+\/(\d+x\d+)\//,
    sizes: [
      // Players
      27, 75, 324,
    ],
    type: ImageConfigType.PATH,
    path: /\/\d+x\d+\//,
  },
  {
    match: /editorial(?:[-.]\w+)?\.uefa\.com/,
    sizes: [
      // Editorial
      158, 276, 280, 369, 490, 556, 656, 988, 2048,
    ],
    type: ImageConfigType.AKAMAI,
    qs: 'imwidth',
  },
  {
    match: /img(?:[-.]\w+)?\.uefa\.com/,
    sizes: [
      // Others
      36, 65, 75, 83, 140, 166, 324, 400, 700, 5000,
    ],
    type: ImageConfigType.AKAMAI,
    qs: 'imwidth',
  },
];

const getRegExp = (value: RegExp | string): RegExp => (value instanceof RegExp ? value : new RegExp(value));

export const getImgConfig = (src: string): ImageConfig => {
  try {
    const srcUrl = new URL(src);
    return imageSizesConfig.find((c) => getRegExp(c.match).test(srcUrl.href));
  } catch {
    return null;
  }
};

export const getWidthUrl = (src: string, width: number, config: ImageConfig): string => {
  if (!width) return src;

  try {
    const srcUrl = new URL(src);
    const pathRegExp = getRegExp(config.path);
    if (config.type === ImageConfigType.PATH && pathRegExp.test(srcUrl.pathname)) {
      const path = srcUrl.pathname.replace(pathRegExp, `/${width}x${width}/`);
      return new URL(path, srcUrl.origin).toString();
    }

    if (src.includes(config.qs)) return src;

    srcUrl.searchParams.append(config.qs, width.toString());
    return srcUrl.toString();
  } catch {
    return src;
  }
};

export const getWidthSrcSet = (src: string, getFrist?: boolean): string => {
  if (!src) return '';

  if (src.endsWith('.svg')) return src;

  const config = getImgConfig(src);
  if (!config?.sizes?.length) return src;

  if (getFrist) return getWidthUrl(src, config.sizes[0], config);

  if (/ \d+w,?/.test(src)) return src;

  return config.sizes.map((width) => `${getWidthUrl(src, width, config)} ${width}w`).join(', ');
};

export const getWidthSrc = (src: string): string => getWidthSrcSet(src, true);

export const observeImageSize = (img: HTMLImageElement, callback: (size: number) => void): ResizeObserver => {
  const debouncedCallback = debounce(callback, 100);
  const resizeObserver = new ResizeObserver((entries) => {
    const {
      contentRect: { width },
    } = entries[0];

    debouncedCallback(width);
  });

  resizeObserver.observe(img);

  return resizeObserver;
};
